import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import Button from 'components/UI/V3/Button/Button';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownFooter } from 'components/UI/V3/Dropdown/Footer/DropdownFooter';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import React from 'react';
import { useMediaQuery } from 'react-responsive';
import styles from './MultiSelectField.module.scss';

export type MultiSelectFieldOption<T extends string | number> = {
    id: T;
    label: string;
};

/* eslint-disable no-unused-vars */
interface MultiSelectFieldProps<T extends string | number> {
    label?: string;
    placeholder: string;
    options: Array<MultiSelectFieldOption<T>>;
    value: Array<MultiSelectFieldOption<T>>;
    disabled?: boolean;
    zIndex?: number;
    maxDropdownHeight?: number;
    emptyStateLabel?: string;
    onChange(value: Array<MultiSelectFieldOption<T>>): void;
}
/* eslint-enable no-unused-vars */

export const MultiSelectField = <T extends string | number>({
    label,
    placeholder,
    options,
    value,
    disabled = false,
    zIndex,
    maxDropdownHeight = 360,
    emptyStateLabel = 'No options available',
    onChange
}: MultiSelectFieldProps<T>) => {
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });
    const [isOpen, setIsOpen] = React.useState(false);
    const triggerRef = React.useRef<HTMLDivElement | null>(null);
    const [triggerWidth, setTriggerWidth] = React.useState<number | undefined>(undefined);
    const [draftValue, setDraftValue] = React.useState<Array<MultiSelectFieldOption<T>>>(value);

    const syncTriggerWidth = React.useCallback(() => {
        const width = triggerRef.current?.getBoundingClientRect().width;
        setTriggerWidth(width && width > 0 ? width : undefined);
    }, []);

    React.useEffect(() => {
        syncTriggerWidth();
    }, [syncTriggerWidth, value.length, options.length]);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleResize = () => syncTriggerWidth();

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isOpen, syncTriggerWidth]);

    React.useEffect(() => {
        if (!disabled) {
            return;
        }

        setIsOpen(false);
    }, [disabled]);

    React.useEffect(() => {
        setDraftValue(value);
    }, [value]);

    const valueForDropdown = React.useMemo(() => {
        return isMobile ? draftValue : value;
    }, [isMobile, draftValue, value]);

    const selectedIds = React.useMemo(() => new Set(valueForDropdown.map(option => option.id)), [valueForDropdown]);

    const optionSelectHandler = React.useCallback(
        (option: MultiSelectFieldOption<T>, checked: boolean) => {
            const nextSelectedIds = new Set(valueForDropdown.map(selected => selected.id));

            if (checked) {
                nextSelectedIds.add(option.id);
            } else {
                nextSelectedIds.delete(option.id);
            }

            const nextValue = options.filter(item => nextSelectedIds.has(item.id));

            if (isMobile) {
                setDraftValue(nextValue);
                return;
            }

            onChange(nextValue);
        },
        [isMobile, onChange, options, valueForDropdown]
    );

    const optionRemoveHandler = React.useCallback(
        (optionId: T) => {
            setIsOpen(false);
            onChange(value.filter(option => option.id !== optionId));
        },
        [onChange, value]
    );

    const openChangeHandler = React.useCallback(
        (nextOpen: boolean) => {
            setIsOpen(nextOpen);

            if (nextOpen && isMobile) {
                setDraftValue(value);
            }
        },
        [isMobile, value]
    );

    const applyHandler = React.useCallback(() => {
        onChange(draftValue);
        setIsOpen(false);
    }, [draftValue, onChange]);

    return (
        <div className={styles.root}>
            {label && <div className={styles.label}>{label}</div>}

            <Dropdown
                open={isOpen}
                onOpenChange={openChangeHandler}
            >
                <DropdownTrigger
                    className={styles.triggerWrapper}
                    disabled={disabled}
                >
                    <div
                        ref={triggerRef}
                        className={clsx(styles.trigger, {
                            [styles.triggerOpen]: isOpen,
                            [styles.triggerDisabled]: disabled
                        })}
                    >
                        <div className={styles.triggerContent}>
                            {value.length === 0 ? (
                                <span className={clsx(styles.triggerText, styles.placeholder)}>{placeholder}</span>
                            ) : (
                                value.map(option => (
                                    <span
                                        key={String(option.id)}
                                        className={styles.chip}
                                    >
                                        {option.label}

                                        <button
                                            type="button"
                                            className={styles.chipRemove}
                                            disabled={disabled}
                                            onMouseDown={event => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                            }}
                                            onClick={event => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                optionRemoveHandler(option.id);
                                            }}
                                            aria-label={`Remove ${option.label}`}
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    </span>
                                ))
                            )}
                        </div>

                        <FontAwesomeIcon
                            icon={isOpen ? faChevronUp : faChevronDown}
                            className={styles.chevron}
                        />
                    </div>
                </DropdownTrigger>

                <DropdownContent
                    zIndex={zIndex}
                    align="start"
                    sideOffset={-1}
                    minWidth={triggerWidth}
                    maxHeight={maxDropdownHeight}
                    className={styles.dropdownContent}
                >
                    {options.length > 0 ? (
                        options.map(option => (
                            <DropdownItem
                                key={String(option.id)}
                                type="checkbox"
                                checked={selectedIds.has(option.id)}
                                className={styles.dropdownItem}
                                onSelect={checked => optionSelectHandler(option, checked)}
                            >
                                {option.label}
                            </DropdownItem>
                        ))
                    ) : (
                        <div className={styles.emptyState}>{emptyStateLabel}</div>
                    )}

                    {isMobile && (
                        <DropdownFooter>
                            <Button
                                size="large"
                                variant="green"
                                label="Apply"
                                onClick={applyHandler}
                            />
                        </DropdownFooter>
                    )}
                </DropdownContent>
            </Dropdown>
        </div>
    );
};

export default MultiSelectField;
